import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mic, Ear, BookOpen, ScrollText, Heart, Wrench,
  Shield, FileText, Trash2, ArrowUpRight, ArrowLeft, CheckCircle2,
  Instagram, Apple, QrCode,
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn } from '../components/ui-bits';
import { QRCodeSVG } from 'qrcode.react';

const LOGO = 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/uldkzlzi_logo%20leitpalel.png';
const OG_IMAGE = LOGO; // reuse for sharing cards
const APP_STORE_URL = 'https://apps.apple.com/app/id6798715115';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.leitpalel.app';
const INSTAGRAM_URL = 'https://www.instagram.com/leitpalel';

// Google Play glyph (inline SVG — no lucide equivalent)
const GooglePlayIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 01-.609-.918V2.732a1 1 0 01.609-.918zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.624a1 1 0 010 1.736l-2.807 1.624L15.192 12l2.506-2.492zm-3.199-3.199l-8.635-8.635 10.937 6.333-2.302 2.302z" />
  </svg>
);

const T = {
  es: {
    back: 'Volver a Desarrollo de Software',
    eyebrow: 'App · Leitpalel',
    heroTitle: 'LEITPALEL',
    heroSubtitle: 'Aprende a rezar, a tu ritmo',
    heroBajada: 'La app del Sidur con karaoke palabra por palabra. Pronunciación ashkenazí y sefaradí. En español, inglés, francés y portugués.',
    ctaApple: 'App Store',
    ctaAppleSub: 'Descargar en',
    ctaPlay: 'Google Play',
    ctaPlaySub: 'Disponible en',
    recommendedLabel: 'Recomendado para ti',
    features: 'Beneficios',
    featuresTitle: 'Todo lo que necesitas para rezar con seguridad.',
    f1: 'Karaoke de rezos',
    f1d: 'El texto se ilumina palabra por palabra mientras escuchas.',
    f2: 'Modo Sinagoga',
    f2d: 'La app escucha el rezo en vivo y te muestra en qué parte van.',
    f3: 'Parashá de la semana',
    f3d: 'Practica tu lectura, ideal para preparar el Bar Mitzvá.',
    f4: 'Sidur completo',
    f4d: 'Según el calendario judío de cada día.',
    f5: 'Tehilim por intención',
    f5d: 'Organizados por salud, parnasá, protección y más.',
    f6: 'Herramientas',
    f6d: 'Conversor de fechas hebreas, guía de tefilín y mucho más.',
    closing: 'Pruébala gratis por 7 días. Cancela cuando quieras.',
    legal: 'Legal',
    legalTitle: 'Todo transparente.',
    legalDesc: 'Leitpalel es operada por Inversiones Tair SpA (Chile). Consulta nuestras políticas antes de instalar.',
    privacyLink: 'Política de Privacidad',
    termsLink: 'Términos y Condiciones',
    deleteLink: 'Eliminar Cuenta',
    contactLink: 'Escribir a info@tair.site',
    followUs: 'Síguenos',
    qrEyebrow: 'Escanea desde tu teléfono',
    qrApple: 'App Store · iPhone / iPad',
    qrPlay: 'Google Play · Android',
    trust1: 'Sin publicidad',
    trust2: 'Sin tracking',
    trust3: 'Voz procesada en tu dispositivo',
    pageTitle: 'Leitpalel – Aprende a rezar, a tu ritmo',
    pageDescription: 'La app del Sidur con karaoke palabra por palabra, modo sinagoga y lecturas de la Torá. Disponible para iPhone y Android.',
  },
  en: {
    back: 'Back to Software Development',
    eyebrow: 'App · Leitpalel',
    heroTitle: 'LEITPALEL',
    heroSubtitle: 'Learn to pray, at your own pace',
    heroBajada: 'The Siddur app with word-by-word karaoke. Ashkenazi and Sephardic pronunciation. In Spanish, English, French and Portuguese.',
    ctaApple: 'App Store',
    ctaAppleSub: 'Download on the',
    ctaPlay: 'Google Play',
    ctaPlaySub: 'Get it on',
    recommendedLabel: 'Recommended for you',
    features: 'Benefits',
    featuresTitle: 'Everything you need to pray with confidence.',
    f1: 'Prayer karaoke',
    f1d: 'The text lights up word by word as you listen.',
    f2: 'Synagogue Mode',
    f2d: 'The app listens to the live prayer and shows you where everyone is.',
    f3: 'Weekly Parashá',
    f3d: 'Practice your reading, perfect to prepare the Bar Mitzvah.',
    f4: 'Full Siddur',
    f4d: 'Following the Jewish calendar day by day.',
    f5: 'Tehilim by intention',
    f5d: 'Organized by health, parnasá, protection and more.',
    f6: 'Tools',
    f6d: 'Hebrew date converter, tefillin guide and much more.',
    closing: 'Try it free for 7 days. Cancel anytime.',
    legal: 'Legal',
    legalTitle: 'Everything transparent.',
    legalDesc: 'Leitpalel is operated by Inversiones Tair SpA (Chile). Read our policies before installing.',
    privacyLink: 'Privacy Policy',
    termsLink: 'Terms of Service',
    deleteLink: 'Delete Account',
    contactLink: 'Write to info@tair.site',
    followUs: 'Follow us',
    qrEyebrow: 'Scan from your phone',
    qrApple: 'App Store · iPhone / iPad',
    qrPlay: 'Google Play · Android',
    trust1: 'No ads',
    trust2: 'No tracking',
    trust3: 'Voice processed on device',
    pageTitle: 'Leitpalel – Learn to pray, at your own pace',
    pageDescription: 'The Siddur app with word-by-word karaoke, synagogue mode and Torah readings. Available for iPhone and Android.',
  },
};

// Detect user's primary platform for highlighting the right CTA
const detectPlatform = () => {
  if (typeof window === 'undefined' || !window.navigator) return 'unknown';
  const ua = (window.navigator.userAgent || '').toLowerCase();
  if (/iphone|ipad|ipod|mac os/.test(ua) && !/android/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
};

// Hook to update <title>, meta description and OG tags dynamically
const useDocumentMeta = ({ title, description, image }) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [attrName, attrVal] = selector.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(attrName, attrVal.replace(/"/g, ''));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:image"]', 'content', image);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:url"]', 'content', 'https://tair.site/leitpalel');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', image);

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, image]);
};

const Leitpalel = () => {
  const { lang } = useLanguage();
  const s = T[lang] || T.es;

  const [platform, setPlatform] = useState('unknown');
  useEffect(() => setPlatform(detectPlatform()), []);

  useDocumentMeta({
    title: s.pageTitle,
    description: s.pageDescription,
    image: OG_IMAGE,
  });

  const features = [
    { icon: Mic, t: s.f1, d: s.f1d },
    { icon: Ear, t: s.f2, d: s.f2d },
    { icon: ScrollText, t: s.f3, d: s.f3d },
    { icon: BookOpen, t: s.f4, d: s.f4d },
    { icon: Heart, t: s.f5, d: s.f5d },
    { icon: Wrench, t: s.f6, d: s.f6d },
  ];

  const appleHighlighted = platform === 'ios';
  const playHighlighted = platform === 'android';

  return (
    <div data-testid="leitpalel-marketing">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 light-beam" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 35%, rgba(201,162,39,0.4), transparent 55%),
                              radial-gradient(circle at 80% 70%, rgba(99,102,241,0.22), transparent 55%)`,
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-12 pt-28 pb-20">
          <FadeIn>
            <Link
              to="/que-hacemos/desarrollo-software"
              className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-amber-300 transition-colors mb-10"
              data-testid="leitpal-back"
            >
              <ArrowLeft className="w-4 h-4" /> {s.back}
            </Link>
          </FadeIn>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeIn>
                <Eyebrow>{s.eyebrow}</Eyebrow>
              </FadeIn>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                className="mt-8 font-display font-extrabold tracking-tight text-white leading-[0.95]"
                style={{ fontSize: 'clamp(3.5rem, 10vw, 8rem)' }}
                data-testid="leitpal-hero-title"
              >
                {s.heroTitle}
              </motion.h1>

              <FadeIn delay={0.2}>
                <h2 className="mt-6 font-serif-i text-amber-300" style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)', lineHeight: '1.1', fontWeight: 400 }}>
                  {s.heroSubtitle}
                </h2>
              </FadeIn>

              <FadeIn delay={0.3}>
                <p className="mt-8 max-w-xl font-body text-base sm:text-lg text-white/70 leading-relaxed">
                  {s.heroBajada}
                </p>
              </FadeIn>

              <FadeIn delay={0.42}>
                <div className="mt-10 flex flex-wrap gap-3">
                  {/* App Store */}
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="leitpal-appstore-cta"
                    aria-label="Descargar en App Store"
                    className={`relative inline-flex items-center gap-3 h-14 px-6 py-3 rounded-full font-semibold text-sm transition-all ${
                      appleHighlighted
                        ? 'bg-white text-[#0a1322] shadow-[0_0_0_1px_rgba(232,176,78,0.55),0_0_40px_rgba(232,176,78,0.3)] scale-[1.02]'
                        : 'bg-white/95 text-[#0a1322] hover:bg-white hover:scale-[1.02]'
                    }`}
                  >
                    <Apple className="w-6 h-6" />
                    <div className="flex flex-col items-start leading-tight">
                      <span className="text-[10px] uppercase tracking-widest text-black/60">{s.ctaAppleSub}</span>
                      <span>{s.ctaApple}</span>
                    </div>
                    {appleHighlighted && (
                      <span className="absolute -top-2 -right-2 bg-amber-300 text-[#0a1322] text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full">
                        ★ {s.recommendedLabel}
                      </span>
                    )}
                  </a>

                  {/* Google Play */}
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="leitpal-playstore-cta"
                    aria-label="Disponible en Google Play"
                    className={`relative inline-flex items-center gap-3 h-14 px-6 py-3 rounded-full font-semibold text-sm transition-all ${
                      playHighlighted
                        ? 'bg-white text-[#0a1322] shadow-[0_0_0_1px_rgba(232,176,78,0.55),0_0_40px_rgba(232,176,78,0.3)] scale-[1.02]'
                        : appleHighlighted
                        ? 'bg-transparent border border-white/20 text-white/90 hover:bg-white/5 hover:border-white/40'
                        : 'bg-white/95 text-[#0a1322] hover:bg-white hover:scale-[1.02]'
                    }`}
                  >
                    <GooglePlayIcon className="w-5 h-5" />
                    <div className="flex flex-col items-start leading-tight">
                      <span className={`text-[10px] uppercase tracking-widest ${appleHighlighted ? 'text-white/50' : 'text-black/60'}`}>{s.ctaPlaySub}</span>
                      <span>{s.ctaPlay}</span>
                    </div>
                    {playHighlighted && (
                      <span className="absolute -top-2 -right-2 bg-amber-300 text-[#0a1322] text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full">
                        ★ {s.recommendedLabel}
                      </span>
                    )}
                  </a>
                </div>

                <p className="mt-5 text-sm text-white/55 flex items-center gap-2" data-testid="leitpal-closing">
                  <CheckCircle2 className="w-4 h-4 text-amber-300/80 flex-shrink-0" />
                  {s.closing}
                </p>
              </FadeIn>

              <FadeIn delay={0.6}>
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust1}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust2}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust3}</span>
                </div>
              </FadeIn>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <FadeIn delay={0.35}>
                <div className="relative">
                  <div className="absolute inset-0 blur-3xl bg-amber-300/25 rounded-full" />
                  <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-[3rem] overflow-hidden shadow-2xl border border-white/10 bg-[#0a1322]">
                    <img
                      src={LOGO}
                      alt="Leitpalel"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 text-[10px] uppercase tracking-widest text-white/40 bg-[#070d18]/80 border border-white/10 rounded-full px-3 py-1.5 backdrop-blur">
                    iOS · Android · iPad
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* QR CODES — SCAN FROM PHONE */}
      <Section className="pt-4 pb-20 md:pb-24" dataTestId="leitpal-qr-section">
        <FadeIn>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-8 md:p-12">
            <div className="flex items-center gap-3 mb-10 justify-center md:justify-start">
              <QrCode className="w-5 h-5 text-amber-300" />
              <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-300/85">{s.qrEyebrow}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Apple QR */}
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leitpal-qr-apple"
                className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-[#0a1322]/60 p-5 md:p-6 hover:border-amber-300/40 hover:bg-amber-300/[0.04] transition-all"
              >
                <div className="flex-shrink-0 bg-white p-3 rounded-xl">
                  <QRCodeSVG
                    value={APP_STORE_URL}
                    size={128}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#0a1322"
                    imageSettings={{
                      src: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%230a1322"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>',
                      height: 32,
                      width: 32,
                      excavate: true,
                    }}
                  />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">{s.ctaAppleSub}</div>
                  <div className="mt-1 font-display font-bold text-white text-xl md:text-2xl">{s.ctaApple}</div>
                  <div className="mt-2 text-xs text-white/55">{s.qrApple}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-amber-300/80 text-xs group-hover:text-amber-300">
                    Abrir enlace <ArrowUpRight className="w-3 h-3 group-hover:rotate-45 transition-transform" />
                  </div>
                </div>
              </a>

              {/* Google Play QR */}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leitpal-qr-play"
                className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-[#0a1322]/60 p-5 md:p-6 hover:border-amber-300/40 hover:bg-amber-300/[0.04] transition-all"
              >
                <div className="flex-shrink-0 bg-white p-3 rounded-xl">
                  <QRCodeSVG
                    value={PLAY_STORE_URL}
                    size={128}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#0a1322"
                    imageSettings={{
                      src: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%230a1322"><path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 01-.609-.918V2.732a1 1 0 01.609-.918zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.624a1 1 0 010 1.736l-2.807 1.624L15.192 12l2.506-2.492zm-3.199-3.199l-8.635-8.635 10.937 6.333-2.302 2.302z"/></svg>',
                      height: 32,
                      width: 32,
                      excavate: true,
                    }}
                  />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">{s.ctaPlaySub}</div>
                  <div className="mt-1 font-display font-bold text-white text-xl md:text-2xl">{s.ctaPlay}</div>
                  <div className="mt-2 text-xs text-white/55">{s.qrPlay}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-amber-300/80 text-xs group-hover:text-amber-300">
                    Abrir enlace <ArrowUpRight className="w-3 h-3 group-hover:rotate-45 transition-transform" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* FEATURES / BENEFITS */}
      <Section className="py-24 md:py-32" dataTestId="leitpal-features">
        <FadeIn><Eyebrow>{s.features}</Eyebrow></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">
            {s.featuresTitle}
          </h2>
        </FadeIn>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <FadeIn key={i} delay={i * 0.06}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover" data-testid={`leitpal-feature-${i + 1}`}>
                  <div className="w-11 h-11 rounded-xl bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="mt-6 font-display font-bold text-lg md:text-xl text-white tracking-tight">{f.t}</h4>
                  <p className="mt-3 font-body text-sm text-white/55 leading-relaxed">{f.d}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* FINAL CTA BAND */}
      <Section className="pb-24 md:pb-32" dataTestId="leitpal-final-cta">
        <FadeIn>
          <div className="rounded-3xl border border-amber-300/30 bg-gradient-to-br from-amber-300/[0.08] to-transparent p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 rounded-full bg-amber-300/15 blur-3xl pointer-events-none" />
            <div className="relative text-center max-w-2xl mx-auto">
              <h3 className="font-display font-extrabold text-3xl md:text-5xl text-white tracking-tight leading-[1.05]">
                {s.closing}
              </h3>
              <div className="mt-10 flex flex-wrap justify-center gap-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 h-14 px-6 py-3 rounded-full bg-white text-[#0a1322] font-semibold text-sm hover:bg-amber-100 transition-colors"
                  data-testid="leitpal-appstore-cta-bottom"
                >
                  <Apple className="w-6 h-6" />
                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-[10px] uppercase tracking-widest text-black/60">{s.ctaAppleSub}</span>
                    <span>{s.ctaApple}</span>
                  </div>
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 h-14 px-6 py-3 rounded-full bg-white text-[#0a1322] font-semibold text-sm hover:bg-amber-100 transition-colors"
                  data-testid="leitpal-playstore-cta-bottom"
                >
                  <GooglePlayIcon className="w-5 h-5" />
                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-[10px] uppercase tracking-widest text-black/60">{s.ctaPlaySub}</span>
                    <span>{s.ctaPlay}</span>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* LEGAL + SOCIAL */}
      <Section className="pb-32" dataTestId="leitpal-legal-section">
        <FadeIn>
          <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-10 md:p-14">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-10">
              <div className="flex-1">
                <Eyebrow>{s.legal}</Eyebrow>
                <h3 className="mt-5 font-display font-extrabold text-2xl md:text-3xl text-white tracking-tight">{s.legalTitle}</h3>
                <p className="mt-4 max-w-2xl font-body text-white/60">{s.legalDesc}</p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/leitpalel/privacy"
                    data-testid="leitpal-marketing-privacy"
                    className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-amber-300 px-4 py-2 rounded-full border border-white/15 hover:border-amber-300/40 transition-colors"
                  >
                    <Shield className="w-4 h-4" /> {s.privacyLink} <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                  <Link
                    to="/leitpalel/terms"
                    data-testid="leitpal-marketing-terms"
                    className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-amber-300 px-4 py-2 rounded-full border border-white/15 hover:border-amber-300/40 transition-colors"
                  >
                    <FileText className="w-4 h-4" /> {s.termsLink} <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                  <Link
                    to="/leitpalel/delete-account"
                    data-testid="leitpal-marketing-delete"
                    className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-amber-300 px-4 py-2 rounded-full border border-white/15 hover:border-amber-300/40 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" /> {s.deleteLink} <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                  <a
                    href="mailto:info@tair.site"
                    className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-amber-300 px-4 py-2 rounded-full border border-white/15 hover:border-amber-300/40 transition-colors"
                  >
                    info@tair.site <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* Instagram card */}
              <div className="md:w-80 flex-shrink-0">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="leitpal-instagram"
                  className="group block rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-amber-300/40 hover:bg-amber-300/[0.03] transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-300/15 to-amber-300/5 border border-amber-300/25 flex items-center justify-center text-amber-300 group-hover:scale-110 transition-transform">
                      <Instagram className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{s.followUs}</div>
                      <div className="mt-1 font-display font-bold text-white text-lg">@leitpalel</div>
                      <div className="mt-2 text-sm text-amber-300/80 inline-flex items-center gap-1">
                        instagram.com/leitpalel
                        <ArrowUpRight className="w-3 h-3 group-hover:rotate-45 transition-transform" />
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-white/40 font-mono uppercase tracking-widest">
              <span>© Leitpalel · Inversiones Tair SpA</span>
              <span>tair.site/leitpalel</span>
            </div>
          </div>
        </FadeIn>
      </Section>
    </div>
  );
};

export default Leitpalel;
